import { createContext, useContext, useEffect, useRef } from "react";

/**
 * A single client-push event delivered from the host to a plugin surface. The host
 * relays the plugin backend's `IClientPushPublisher` events over its realtime
 * channel (SignalR); the transport envelope's extension identity is bound by the
 * host at mount time, so the plugin sees only the event body.
 *
 * SCOPE + ORDERING: which channel/user/group an event concerns is carried INSIDE
 * `payloadJson` by the emitting plugin — the transport envelope intentionally has no
 * group field. Consumers therefore demultiplex + order by their own payload fields
 * (e.g. a per-channel sequence in the payload), NOT by {@link eventSequence}, which
 * is per-group at the host and would produce false gaps when multiple groups
 * multiplex over one connection.
 */
export interface ClientPushEvent
{
    /** Plugin-defined discriminator, e.g. `"chatMessageReceived"`. */
    eventType: string;
    /** Raw JSON payload authored by the plugin backend. */
    payloadJson: string;
    /**
     * Host per-group monotonic sequence. Advisory only — do NOT use for
     * cross-group gap detection (see the scope note above).
     */
    eventSequence: number;
}

export interface ClientPushSubscribeOptions
{
    /**
     * Opaque group names to enrol in (e.g. `"chat:channel:{id}"`). The host
     * authorises each subscription via the plugin's `authorize-subscription` tool
     * and enforces org/extension isolation — a plugin cannot subscribe outside its
     * own extension + organisation.
     */
    groups: string[];
    /** Called for each delivered (non-resync) event for the subscribed groups. */
    onEvent: (event: ClientPushEvent) => void;
    /**
     * Called when the host signals a gap/resync for the subscribed groups (a
     * dropped-event backpressure signal, or a reconnect). The consumer should
     * re-fetch authoritative state (e.g. a delta/cold-load) rather than trusting
     * incremental events.
     */
    onResync?: () => void;
}

/**
 * Host-provided channel for realtime server-push. The channel is already scoped to
 * the mounted surface's extension + organisation (bound by the host from the trusted
 * mount descriptor — a plugin CANNOT widen it), so {@link subscribe} takes only
 * opaque group names and returns an unsubscribe function.
 */
export interface ClientPushChannel
{
    subscribe(options: ClientPushSubscribeOptions): () => void;
}

/**
 * `null` = no host channel (standalone/mock, or a host that predates client-push) →
 * {@link useClientPushSubscription} is inert. Provided by
 * {@link ExtensionRuntimeProvider}'s optional `clientPush` prop.
 */
export const ClientPushContext = createContext<ClientPushChannel | null>(null);

export interface UseClientPushSubscriptionOptions
{
    /** Opaque groups to subscribe. Changing the SET re-subscribes; identity/order changes alone do not. */
    groups: string[];
    onEvent: (event: ClientPushEvent) => void;
    onResync?: () => void;
    /** Gate the subscription (e.g. until an id is known). Default `true`. */
    enabled?: boolean;
}

/**
 * Subscribe a plugin surface to host client-push events for `groups`.
 *
 * Inert (no-op) when no host channel is present (standalone/mock), when `enabled` is
 * false, or when `groups` is empty. Re-subscribes when the group set changes and
 * unsubscribes on unmount. Callback identities are held in refs, so passing new
 * inline `onEvent`/`onResync` closures every render does NOT churn the subscription.
 */
export function useClientPushSubscription(options: UseClientPushSubscriptionOptions): void
{
    const { groups, onEvent, onResync, enabled = true } = options;
    const channel = useContext(ClientPushContext);

    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;
    const onResyncRef = useRef(onResync);
    onResyncRef.current = onResync;

    // Normalise (dedupe + sort) so the effect re-runs only when the group SET actually
    // changes — reordering the same groups, or passing a fresh array literal of the
    // same set each render, must NOT churn the subscription.
    const normalizedGroups = [...new Set(groups)].sort();
    // JSON-encode (not space-join) so group names containing a delimiter can't alias
    // distinct sets to the same key (e.g. ["a b"] vs ["a","b"]).
    const groupsKey = JSON.stringify(normalizedGroups);

    useEffect(() =>
    {
        if (!channel || !enabled || normalizedGroups.length === 0)
        {
            return;
        }
        const unsubscribe = channel.subscribe({
            groups: normalizedGroups,
            onEvent: (event) => onEventRef.current(event),
            onResync: () => onResyncRef.current?.(),
        });
        return unsubscribe;
        // normalizedGroups is captured via groupsKey; callbacks via refs — intentionally excluded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, enabled, groupsKey]);
}
